import { useEffect, useState } from "react"
import type { ReactElement } from "react"

import type { Document } from "@/api/models"

import { LazyPdfViewer } from "@/features/chat/components/lazy-pdf-viewer"
import { DocxViewer } from "@/features/file-viewer/components/docx-viewer"
import { ExcelViewer } from "@/features/file-viewer/components/excel-viewer"
import { FallbackViewer } from "@/features/file-viewer/components/fallback-viewer"
import { HtmlViewer } from "@/features/file-viewer/components/html-viewer"
import { MarkdownViewer } from "@/features/file-viewer/components/markdown-viewer"
import { TextViewer } from "@/features/file-viewer/components/text-viewer"
import { useCitationFileDownload } from "@/hooks/use-citation-file"
import { formatIsoDateToFullDate, isISODate } from "@/lib/dates"
import { logger } from "@/lib/logger"
import { DocumentService } from "@/services/document-service"
import { SettingsService } from "@/services/settings-service"
import { formatCitationDisplay, getCitationSourceForLookup } from "@/utils/citation-utils"
import { extractCleanFilename } from "@/utils/file-utils"

export type ExtraDataItem = {
  display_name: string
  add_to_prompt: boolean
  add_to_ui: boolean
  values: string[]
  /**
   * How the value should be displayed: "text", "html" (a SharePoint rich-text column, which
   * arrives as markup), or "link". Absent on rows stored before the mode existed -- fall back to
   * `links` for those (#3008).
   */
  render?: string
  links?: boolean
  link_titles?: string[]
}

type Renderers = {
  [key: string]: (src: string, options?: RenderComponentOptions) => ReactElement
}

type RenderComponentOptions = {
  quoteSnippet?: string | null
  chunkSnippet?: string | null
  highlightScrollContainer?: HTMLElement | null
  onSlowRender?: () => void
}

export function useCitation() {
  const downloadMutation = useCitationFileDownload()

  const [currentViewedDocumentUrl, setCurrentViewedDocumentUrl] = useState<string | null>(null)
  const [currentViewedDocumentTitle, setCurrentViewedDocumentTitle] = useState<string | null>(null)
  const [openAnalysisPanel, setOpenAnalysisPanel] = useState(false)
  const [openMetadataDialog, setOpenMetadataDialog] = useState(false)
  const [extraData, setExtraData] = useState<ExtraDataItem[] | null>(null)
  const isUrl = (link: string): boolean => {
    try {
      new URL(link)
      return link.startsWith("http")
    } catch {
      return false
    }
  }

  function isExtraDataItem(item: unknown): item is ExtraDataItem {
    return (
      typeof item === "object" &&
      item !== null &&
      "display_name" in item &&
      "add_to_prompt" in item &&
      "values" in item
    )
  }

  function getDocumentExtraData(
    librariesAndDocuments: [string, Document][] | undefined,
    citationDocumentId: string
  ) {
    if (!librariesAndDocuments) {
      return null
    }

    const baseCitationDocumentId = getCitationSourceForLookup(citationDocumentId)

    // Find the document with the same sourcefile as the citation
    const document = librariesAndDocuments.find(([libraryId, document]) => {
      const ragDocId = `${libraryId}:${document.sourcefile}`
      return ragDocId === baseCitationDocumentId
    })

    const extraData = document?.[1]?.extra_data || null
    if (!Array.isArray(extraData) || !extraData.every(isExtraDataItem) || extraData.length < 1) {
      return null
    }
    return extraData
  }

  const renderers: Renderers = {
    pdf: (src: string, options = {}) => (
      <LazyPdfViewer
        fileUrl={src}
        highlightChunk={options.chunkSnippet ?? null}
        highlightQuote={options.quoteSnippet ?? null}
        highlightScrollContainer={options.highlightScrollContainer}
      />
    ),
    png: (src: string) => (
      <img alt="custom" className="h-[450px] max-w-full object-contain" src={src} />
    ),
    docx: (src: string, options = {}) => (
      <DocxViewer
        fileUrl={src}
        highlightChunk={options.chunkSnippet ?? null}
        highlightQuote={options.quoteSnippet ?? null}
        highlightScrollContainer={options.highlightScrollContainer}
        onSlowRender={options.onSlowRender}
      />
    ),
    xlsx: (src: string, options = {}) => (
      <ExcelViewer
        fileUrl={src}
        highlightChunk={options.chunkSnippet ?? null}
        highlightQuote={options.quoteSnippet ?? null}
        highlightScrollContainer={options.highlightScrollContainer}
        onSlowRender={options.onSlowRender}
      />
    ),
    xls: (src: string, options = {}) => (
      <ExcelViewer
        fileUrl={src}
        highlightChunk={options.chunkSnippet ?? null}
        highlightQuote={options.quoteSnippet ?? null}
        highlightScrollContainer={options.highlightScrollContainer}
        onSlowRender={options.onSlowRender}
      />
    ),
    html: (src: string, options = {}) => (
      <HtmlViewer
        fileUrl={src}
        highlightChunk={options.chunkSnippet ?? null}
        highlightQuote={options.quoteSnippet ?? null}
        highlightScrollContainer={options.highlightScrollContainer}
      />
    ),
    txt: (src: string, options = {}) => (
      <TextViewer
        fileUrl={src}
        highlightChunk={options.chunkSnippet ?? null}
        highlightQuote={options.quoteSnippet ?? null}
        highlightScrollContainer={options.highlightScrollContainer}
      />
    ),
    md: (src: string, options = {}) => (
      <MarkdownViewer
        fileUrl={src}
        highlightChunk={options.chunkSnippet ?? null}
        highlightQuote={options.quoteSnippet ?? null}
        highlightScrollContainer={options.highlightScrollContainer}
      />
    ),
    json: (src: string, options = {}) => (
      <TextViewer
        fileUrl={src}
        highlightChunk={options.chunkSnippet ?? null}
        highlightQuote={options.quoteSnippet ?? null}
        highlightScrollContainer={options.highlightScrollContainer}
      />
    ),
    "": (src: string) => <FallbackViewer fileUrl={src} />,
  }

  function getStandaloneDocumentIdFromCitationPath(url: string | null): string | null {
    if (!url) return null
    const decodedUrl = decodeURIComponent(url)
    const pathMatch = decodedUrl.match(/\/content\/doc\/([^/?]+)/)
    if (!pathMatch?.[1]) {
      return null
    }

    const decodedCitation = decodeURIComponent(pathMatch[1]).split("#page=")[0]
    if (!decodedCitation.startsWith("doc:")) {
      return null
    }

    const documentId = decodedCitation.slice(4).trim()
    return documentId || null
  }

  function isStandaloneDocumentCitationPath(url: string | null): boolean {
    return getStandaloneDocumentIdFromCitationPath(url) !== null
  }

  useEffect(() => {
    const documentId = getStandaloneDocumentIdFromCitationPath(currentViewedDocumentUrl)
    if (!documentId) {
      setCurrentViewedDocumentTitle(null)
      return
    }

    let cancelled = false

    const loadDocumentTitle = async () => {
      try {
        const settingsService = SettingsService.getInstance()
        const document = await DocumentService.getDocument(documentId, settingsService.token)
        if (!cancelled) {
          setCurrentViewedDocumentTitle(document.title || "Document")
        }
      } catch {
        if (!cancelled) {
          setCurrentViewedDocumentTitle("Document")
        }
      }
    }

    void loadDocumentTitle()

    return () => {
      cancelled = true
    }
  }, [currentViewedDocumentUrl])

  function getFileExtension(url: string): string {
    if (!url) return ""
    const decodedUrl = decodeURIComponent(url)
    if (isStandaloneDocumentCitationPath(decodedUrl)) {
      return "md"
    }
    const urlWithoutPageParam = decodedUrl.split("#page=")[0]
    return urlWithoutPageParam.split(".").pop()?.toLowerCase() ?? ""
  }

  function renderComponent(src: string, options: RenderComponentOptions = {}): ReactElement {
    const decodedUrl = decodeURIComponent(src)
    const quoteSnippet = options.quoteSnippet ?? null
    const chunkSnippet = options.chunkSnippet ?? null
    const highlightScrollContainer = options.highlightScrollContainer
    const onSlowRender = options.onSlowRender
    if (isStandaloneDocumentCitationPath(decodedUrl)) {
      return (
        <MarkdownViewer
          fileUrl={decodedUrl}
          highlightChunk={chunkSnippet}
          highlightQuote={quoteSnippet}
          highlightScrollContainer={highlightScrollContainer}
          titleOverride={currentViewedDocumentTitle || "Document"}
        />
      )
    }
    const ext = getFileExtension(src)
    const renderer = ext in renderers ? renderers[ext] : renderers[""]
    return renderer(decodedUrl, {
      quoteSnippet,
      chunkSnippet,
      highlightScrollContainer,
      onSlowRender,
    })
  }

  function getCleanFileName(path: string): string | undefined {
    if (!path) return undefined

    // Remove page parameter if present
    const basePath = path.split("#page=")[0] || path

    // First try to extract using our utility function
    const cleanName = extractCleanFilename(basePath)

    return cleanName
  }

  async function handleDownloadClicked(path: string, downloadFileName?: string) {
    try {
      // Get the file name for the download. An opaque content locator carries
      // no filename to recover — its last path segment is "original" — so the
      // caller supplies the cited file's real name when it knows it.
      const fileName = downloadFileName?.trim() || getCleanFileName(path) || "download"

      // Use React Query mutation to download file
      const blob = await downloadMutation.mutateAsync(path)

      // Create a download link and trigger it
      const url = window.URL.createObjectURL(blob)
      const a = document.createElement("a")
      a.href = url
      a.download = fileName
      document.body.appendChild(a)
      a.click()

      // Clean up
      window.URL.revokeObjectURL(url)
      document.body.removeChild(a)
    } catch (error) {
      logger.error("Error downloading file:", error as Error)
      // Error toast is handled by the mutation hook
    }
  }

  function formatIsoDateInExtraDataItem(item: ExtraDataItem): ExtraDataItem {
    return {
      ...item,
      values: item.values.map((value) =>
        isISODate(value) ? formatIsoDateToFullDate(value) : value
      ),
    }
  }

  function setFormattedExtraData(data: ExtraDataItem[] | null) {
    if (!data) {
      setExtraData(null)
      return
    }
    const formattedData = data.map(formatIsoDateInExtraDataItem)
    setExtraData(formattedData)
  }

  return {
    isUrl,
    getDocumentExtraData,
    renderComponent,
    currentViewedDocumentUrl,
    setCurrentViewedDocumentUrl,
    handleDownloadClicked,
    openAnalysisPanel,
    setOpenAnalysisPanel,
    openMetadataDialog,
    setOpenMetadataDialog,
    extraData,
    setExtraData: setFormattedExtraData,
    formatCitationDisplay,
  }
}
